import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LEVELS, PLANETS, REGIONS, loadContent, regionForPlanet } from '@playnets/data';
import { useUi } from './store';
import { loadSave, recordClear, starsFor, writeSave } from './save';
import PlanetSelect from './screens/PlanetSelect';
import SystemMap from './screens/SystemMap';
import Armoury from './screens/Armoury';
import Battle from './screens/Battle';
import Results from './screens/Results';
import Margins from './components/Margins';
import { applyRegionTheme } from './theme';

export default function App(): JSX.Element {
  const screen = useUi((s) => s.screen);
  const planetId = useUi((s) => s.planetId);
  const levelId = useUi((s) => s.levelId);
  const [save, setSave] = useState(loadSave);
  /**
   * A first-time player goes straight into a level. Making someone pick a
   * planet and a node on a map before they've seen the game is asking them to
   * make decisions with nothing to base them on — the map earns its place once
   * they've won something and there's somewhere to go.
   */
  const booted = useRef(false);
  useEffect(() => {
    if (booted.current) return;
    booted.current = true;
    // Returning players land on the map, not the planet picker: picking a
    // planet is a rare, deliberate act, and the map is where the next thing
    // to do lives.
    useUi.getState().setScreen(Object.keys(save.stars).length === 0 ? 'battle' : 'map');
    // Only the very first mount; afterwards the player drives navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  /** A fresh seed per run — the sim itself stays deterministic given one. */
  const [seed, setSeed] = useState(() => (Date.now() ^ 0x5f3759df) >>> 0);
  const [outcome, setOutcome] = useState<{
    won: boolean;
    stars: number;
    damage: number;
  } | null>(null);
  const [earned, setEarned] = useState(0);

  const content = useMemo(() => loadContent(), []);
  const planet = PLANETS.find((p) => p.id === planetId) ?? PLANETS[0]!;
  const level = LEVELS.find((l) => l.id === levelId) ?? LEVELS[0]!;
  // On the menus the region follows the planet you're looking at; in a level it
  // follows the level, so the whole page is already the right colour when the
  // playfield appears.
  const region =
    screen === 'battle' || screen === 'results'
      ? (REGIONS.find((r) => r.id === level.region) ?? REGIONS[0]!)
      : regionForPlanet(planetId);
  const stageRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    applyRegionTheme(stageRef.current, region.id);
  }, [region.id]);
  const evolutions = useMemo(
    () => save.evolutions[planetId] ?? [],
    [save.evolutions, planetId],
  );

  const startRun = useCallback(() => {
    setSeed((Date.now() ^ 0x9e3779b9) >>> 0);
    setOutcome(null);
    useUi.getState().openSheet(null);
    useUi.getState().setPaused(false);
    useUi.getState().setScreen('battle');
  }, []);

  const finishRun = useCallback(
    (won: boolean, damage: number, coreMax: number) => {
      const stars = won ? starsFor(damage, coreMax) : 0;
      setOutcome({ won, stars, damage });
      if (won) {
        const next = recordClear(save, levelId, stars);
        setSave(next);
        writeSave(next);
        setEarned(next.jewels - save.jewels);
      }
    },
    [levelId, save],
  );

  return (
    <div className="stage" ref={stageRef}>
      <Margins side="left" planet={planet} level={level} screen={screen} region={region} />

      <main className="plate">
        {screen === 'select' && (
          <PlanetSelect save={save} onSave={setSave} onNext={() => useUi.getState().setScreen('map')} />
        )}
        {screen === 'map' && <SystemMap save={save} onStart={startRun} />}
        {screen === 'armoury' && <Armoury save={save} onSave={setSave} />}
        {screen === 'battle' && (
          <Battle
            planetId={planetId}
            levelId={levelId}
            evolutions={evolutions}
            unlockedStructures={save.unlockedStructures}
            seed={seed}
            content={content}
            onFinish={finishRun}
            onRestartLevel={startRun}
            onExit={() => {
              useUi.getState().setPaused(false);
              useUi.getState().setScreen('map');
            }}
          />
        )}
        {screen === 'results' && (
          <Results
            outcome={outcome}
            earned={earned}
            onRetry={startRun}
            onMap={() => {
              // Preselect what the win just opened, so the map lands on the
              // new node rather than on the one you've already beaten.
              if (outcome?.won) {
                const next = level.next[0];
                if (next) useUi.getState().setLevel(next);
              }
              useUi.getState().setScreen('map');
            }}
          />
        )}
      </main>

      <Margins side="right" planet={planet} level={level} screen={screen} region={region} />

      <div className="rotate-me">Playnets is portrait only — rotate your device</div>
    </div>
  );
}
